import React from 'react'
import { MenuItem } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLocationDot,
  faMotorcycle,
  faPersonWalking,
} from '@fortawesome/free-solid-svg-icons'
import { CustomInput } from '../../../components/custom-input/CustomInput'
import L from 'leaflet'
import { ILocation } from '../../../types/locations'
import { haptic } from '../../../utils/fx'

interface LocationSelectorProps {
  location: ILocation | null
  setLocation: (value: ILocation | null) => void
  takeaway: boolean
  setTakeaway: (value: boolean) => void
  locations: ILocation[]
  errorState: { location: boolean }
  t: (key: string) => string
  setPosition: (pos: L.LatLng | null) => void
  setCoordinates: (coords: L.LatLngExpression) => void
  getTranslatedName: (item: ILocation) => string
}

const RESTAURANT = { lat: 48.331216, lng: 26.87127 }

const LocationSelector: React.FC<LocationSelectorProps> = ({
  location,
  setLocation,
  takeaway,
  setTakeaway,
  locations,
  errorState,
  t,
  setPosition,
  setCoordinates,
  getTranslatedName,
}) => {
  const changeMode = (pickup: boolean) => {
    if (pickup === takeaway) return

    haptic(8)
    setTakeaway(pickup)

    if (pickup) {
      setLocation(null)
      setPosition(RESTAURANT as L.LatLng)
      setCoordinates(RESTAURANT)
    } else {
      setPosition(null)
    }
  }

  return (
    <div className='checkout-page__fields'>
      <div
        className={`checkout-page__segmented${takeaway ? ' is-right' : ''}`}
        role='tablist'
      >
        <span className='checkout-page__segmented-thumb' />
        <button
          role='tab'
          aria-selected={!takeaway}
          onClick={() => changeMode(false)}
        >
          <FontAwesomeIcon icon={faMotorcycle} /> {t('DELIVERY')}
        </button>
        <button
          role='tab'
          aria-selected={takeaway}
          onClick={() => changeMode(true)}
        >
          <FontAwesomeIcon icon={faPersonWalking} /> {t('PICKUP')}
        </button>
      </div>

      {!takeaway && (
        <CustomInput
          select
          fullWidth
          error={!errorState.location}
          helperText={!errorState.location ? t('FIELD_REQUIRED') : undefined}
          value={(location || '') as unknown as string}
          label={t('LOCATION')}
          variant='filled'
          SelectProps={{
            MenuProps: {
              PaperProps: { sx: { borderRadius: '18px', maxHeight: 360 } },
            },
          }}
          onChange={(e) => {
            setLocation(e.target.value as unknown as ILocation)
            setTakeaway(false)
          }}
        >
          {locations.map((loc, index) => (
            <MenuItem
              key={index}
              value={loc as unknown as string}
              sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', gap: 2 }}
            >
              <span>{getTranslatedName(loc)}</span>
              {loc.delivery_price !== undefined && (
                <small style={{ opacity: 0.55 }}>{loc.delivery_price} lei</small>
              )}
            </MenuItem>
          ))}
        </CustomInput>
      )}

      <button
        className='checkout-page__link'
        onClick={(e) => {
          e.preventDefault()
          setPosition(RESTAURANT as L.LatLng)
          setCoordinates(RESTAURANT)
        }}
      >
        <FontAwesomeIcon icon={faLocationDot} /> {t('LOCATION_MAP')}
      </button>
    </div>
  )
}

export default LocationSelector
